import { Link } from 'react-router-dom'
import { AlertTriangle, Flag, Search, X } from 'lucide-react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import type {
  AvailabilityMode,
  Granularity,
  MonthFilter,
  MonthFilterMap,
  WorkerMonthCell,
  WorkerView,
} from '@/types/ui/resource-availability'
import { WorkerActionsMenu } from '@/components/resource-availability/WorkerActionsMenu'
import { MonthFilterPopover } from '@/components/resource-availability/MonthFilterPopover'
import { TimeOffCellBadge } from '@/components/resource-availability/TimeOffCellBadge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { testId } from '@/lib/utils/test-id'
import {
  formatDayLabelWithYear,
  heatmapClasses,
  isCurrentMonth as isCurrentMonthIso,
  isCurrentWeek,
  monthLabel,
  monthsInRange,
  resolveRowEndMarker,
  weekDayRange,
  weekGroupHeader,
  weekLabel,
  weeksInRange,
} from '@/lib/utils/availability-utils'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'

/** Marker used in place of a real `WorkerMonthCell` for columns that fall inside the
 *  current incremental-fetch range. Distinct from "missing data" — pending means we're
 *  *expecting* data for this column and should render a skeleton. */
interface PendingCell {
  monthStart: string
  monthLabel: string
  isCurrentMonth: boolean
  pending: true
}
type DisplayCell = WorkerMonthCell | PendingCell

function isPendingCell(c: DisplayCell): c is PendingCell {
  return (c as PendingCell).pending === true
}

interface PendingRange {
  startIso: string
  endIso: string
}

interface AvailabilityHeatmapProps {
  workers: WorkerView[]
  mode: AvailabilityMode
  granularity?: Granularity
  toggleQuery?: string
  monthFilters?: MonthFilterMap
  monthLabelsForFilters?: WorkerMonthCell[]
  onMonthFilterChange?: (monthIndex: number, filter: MonthFilter | undefined) => void
  /** When provided, each row gets a selection checkbox feeding the team-timeline view. */
  selectedTeamIds?: number[]
  onToggleTeamMember?: (userProfileId: number) => void
  /** Header-level select-all toggle. Caller is the source of truth for what "all" means
   *  (the page operates over the full filtered list, not just the current heatmap page),
   *  so it supplies both the current checked state and the action. */
  allSelected?: boolean
  onToggleAll?: () => void
  /** Inline name search rendered inside the Worker header. Provide both to enable. */
  nameFilter?: string
  onNameFilterChange?: (value: string) => void
  /** Optional view range. Cells with bucketStart outside `[viewStart, viewEnd]` are
   *  hidden — no refetch; same payload, narrower display. */
  viewStart?: string
  viewEnd?: string
  /** Current incremental-fetch range. Columns whose `monthStart` falls in this range
   *  render as skeleton placeholders. Cleared when the fetch completes. */
  pendingRange?: PendingRange | null
  /** Resolves a certification id to its display label for the expired-certification badge's
   *  tooltip. Falls back to a generic label when omitted. */
  resolveCertificationLabel?: (idStr: string) => string
  /** The certification filter currently applied on the page. Gates the expired-certification badge. */
  selectedCertifications?: string[]
}

export function AvailabilityHeatmap({
  workers,
  mode,
  granularity = 'monthly',
  toggleQuery,
  monthFilters,
  monthLabelsForFilters,
  onMonthFilterChange,
  selectedTeamIds,
  onToggleTeamMember,
  allSelected,
  onToggleAll,
  nameFilter,
  onNameFilterChange,
  viewStart,
  viewEnd,
  pendingRange,
  resolveCertificationLabel,
  selectedCertifications,
}: AvailabilityHeatmapProps) {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.heatmap
  const tBar = dictionary?.resourceAvailability?.filterBar
  const searchEnabled = !!onNameFilterChange
  const isWeekly = granularity === 'weekly'
  // Today as yyyy-MM-dd, for the past/future tense of each row's end-date chip. Computed once
  // per render from the *local* calendar date: "today" here means the viewer's day, unlike every
  // other comparison in this module, which pits two payload date strings against each other.
  // toISOString would roll over to tomorrow 4–8h before midnight in Canadian zones, flipping a
  // chip to "Ended" while it's still the worker's last day locally.
  const now = new Date()
  const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

  // The view range may extend past whatever's currently merged on the FE — that's exactly
  // when an incremental fetch is in flight. Compute the *expected* column starts from the
  // view range so columns outside the merged data get rendered as skeletons rather than
  // silently disappearing while the fetch lands.
  const expectedStarts: string[] | null = (() => {
    if (!viewStart || !viewEnd) return null
    return isWeekly ? weeksInRange(viewStart, viewEnd) : monthsInRange(viewStart, viewEnd)
  })()

  const isPendingStart = (monthStart: string): boolean => {
    if (!pendingRange) return false
    return monthStart >= pendingRange.startIso && monthStart <= pendingRange.endIso
  }

  const makePendingCell = (monthStart: string): PendingCell => ({
    monthStart,
    monthLabel: isWeekly ? weekLabel(monthStart) : monthLabel(monthStart),
    isCurrentMonth: isWeekly ? isCurrentWeek(monthStart) : isCurrentMonthIso(monthStart),
    pending: true,
  })

  const cellsForWorker = (w: WorkerView): DisplayCell[] => {
    const base = isWeekly ? w.weeklyCells : w.months
    // No expected list (no view range) — fall back to the worker's loaded cells, no
    // skeletons (only used in legacy "show whatever the data covers" callers).
    if (!expectedStarts) return base
    const byStart = new Map(base.map(c => [c.monthStart, c]))
    return expectedStarts.map(start => {
      const cell = byStart.get(start)
      if (cell) return cell
      // Not in the worker's loaded set. If it's in the pending range we know one's on the
      // way; otherwise it's still a loading-shaped slot (the merged data hasn't covered
      // this start yet but it will after the in-flight fetch resolves).
      if (isPendingStart(start)) return makePendingCell(start)
      // Defensive: if a slot is in the view but not in any known range, treat it as pending
      // anyway. Avoids a scenario where the heatmap silently truncates.
      return makePendingCell(start)
    })
  }

  const headerCells: DisplayCell[] = expectedStarts
    ? (() => {
        // Reuse a worker's cells to populate `isCurrentMonth` accurately for loaded
        // columns; synthesize pending placeholders elsewhere.
        const w = workers[0]
        const base = w ? (isWeekly ? w.weeklyCells : w.months) : []
        const byStart = new Map(base.map(c => [c.monthStart, c]))
        return expectedStarts.map(start => byStart.get(start) ?? makePendingCell(start))
      })()
    : (monthLabelsForFilters ?? (workers[0] ? cellsForWorker(workers[0]).filter((c): c is WorkerMonthCell => !isPendingCell(c)) : []))

  const suffix = toggleQuery ? `?${toggleQuery}` : ''
  // Strings for the per-cell umbrella (PTO / stat holiday) hover. Built once per render and
  // shared across every cell badge.
  const timeOffStrings = {
    heading: t?.timeOffHeading,
    ptoLabel: t?.timeOffPtoLabel,
    statLabel: t?.timeOffStatLabel,
    halfDaySuffix: t?.timeOffHalfDaySuffix,
    iconAria: t?.timeOffIconAria,
  }
  const filtersEnabled = !!onMonthFilterChange
  const selectionEnabled = !!onToggleTeamMember
  const selectedSet = new Set(selectedTeamIds ?? [])
  const selectedCertificationSet = new Set(selectedCertifications ?? [])

  // Month boundary markers: in weekly mode draw a left border on the first cell of each
  // new month so users don't lose context while scrolling. Pre-compute the index→show-border
  // map once per render.
  const monthBoundaries = new Set<number>()
  if (isWeekly) {
    let prevMonthYM: string | null = null
    headerCells.forEach((c, i) => {
      const ym = c.monthStart.slice(0, 7)
      if (ym !== prevMonthYM) {
        if (prevMonthYM !== null) monthBoundaries.add(i)
        prevMonthYM = ym
      }
    })
  }

  // Group weekly cells by their week-start month so the top header tier can span the
  // weeks of each month with a single label. Buckets are ordered; each group records
  // its column count for colSpan.
  interface MonthGroup { yearMonth: string; monthStartIso: string; count: number }
  const monthGroups: MonthGroup[] = []
  if (isWeekly) {
    for (const c of headerCells) {
      const ym = c.monthStart.slice(0, 7)
      const last = monthGroups[monthGroups.length - 1]
      if (last && last.yearMonth === ym) {
        last.count++
      } else {
        // Use the month-start (YYYY-MM-01) of this week's Monday for the label.
        const monthStartIso = `${ym}-01`
        monthGroups.push({ yearMonth: ym, monthStartIso, count: 1 })
      }
    }
  }

  // Year groups span every column of a given calendar year so users can read year context
  // at a glance — especially important now that windows can extend arbitrarily across
  // multi-year ranges. Driven off headerCells' monthStart (year is the first 4 chars).
  interface YearGroup { year: string; count: number }
  const yearGroups: YearGroup[] = []
  for (const c of headerCells) {
    const year = c.monthStart.slice(0, 4)
    const last = yearGroups[yearGroups.length - 1]
    if (last && last.year === year) last.count++
    else yearGroups.push({ year, count: 1 })
  }
  // Column indexes that mark the first cell of a new year (after the first year). Drives
  // a heavier left border on every header tier AND on body data cells at that column, so
  // the year divider reads as one continuous line through the table — not just a stub at
  // the year tier. Distinct from monthBoundaries (weekly-only month dividers, lighter).
  const yearBoundaryIndices = new Set<number>()
  {
    let cum = 0
    for (let i = 0; i < yearGroups.length; i++) {
      if (i > 0) yearBoundaryIndices.add(cum)
      cum += yearGroups[i].count
    }
  }
  // Header tier count.
  // - Monthly: year (top) + per-column month name = 2 tiers.
  // - Weekly: month-with-year-suffix + per-column day-range = 2 tiers (no separate year
  //   tier; the inline ’YY suffix on each month group carries the year, which is more
  //   robust against horizontal scroll than a wide year-tier span centered off-screen).
  const headerTiers = 2

  // Single source of truth for the per-column boundary border. Year wins over month so a
  // year transition (which is always also a month transition) renders one heavier line
  // instead of two stacked at different opacities.
  const boundaryBorderClass = (idx: number, includeMonth: boolean): string => {
    if (yearBoundaryIndices.has(idx)) return 'border-l-2 border-l-muted-foreground/50'
    if (includeMonth && monthBoundaries.has(idx)) return 'border-l-2 border-l-muted-foreground/30'
    return ''
  }

  // Selection column lives to the left of the Worker column when team-timeline selection
  // is wired up. Header cell carries the select-all toggle; body cells carry per-row
  // checkboxes. Both `<th>` and the matching `<td>`s are sticky-left so they stay frozen
  // during horizontal scroll. SELECTION_COL_WIDTH must match the Tailwind class on both
  // sides (header w-12; worker `<th>` left-12) — ~48px is enough for the checkbox + padding.
  const selectionHeaderCell = selectionEnabled ? (
    <th
      rowSpan={headerTiers}
      className="sticky left-0 z-10 bg-muted p-0 w-12 min-w-12"
    >
      {onToggleAll && (
        <div className="flex items-center justify-center px-3 py-2">
          <Checkbox
            checked={!!allSelected}
            onCheckedChange={() => onToggleAll()}
            aria-label={t?.selectAllAria || 'Select all workers'}
            className="shrink-0"
          />
        </div>
      )}
    </th>
  ) : null

  // Worker label `<th>`. Shared between modes — placed in the year `<tr>` for monthly
  // and the month `<tr>` for weekly so it always sits in the first header row and rowSpans
  // the second.
  const workerHeaderCell = (
    <th
      rowSpan={headerTiers}
      className={cn(
        'sticky z-10 bg-muted text-left text-[10px] uppercase tracking-wide font-medium text-muted-foreground px-4 py-2 w-44 align-bottom',
        selectionEnabled ? 'left-12' : 'left-0'
      )}
    >
      <div className="flex flex-col gap-1.5">
        <span>{t?.workerHeader || 'Worker'}</span>
        {searchEnabled && (
          <div className="relative normal-case tracking-normal">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              value={nameFilter ?? ''}
              onChange={(e) => onNameFilterChange?.(e.target.value)}
              placeholder={tBar?.searchPlaceholder || 'Search worker'}
              aria-label={tBar?.searchAriaLabel || 'Filter workers by name'}
              className="h-7 pl-6 pr-6 text-[11px] font-normal text-foreground"
              data-testid={testId('resource-availability-name-filter')}
            />
            {nameFilter && (
              <button
                type="button"
                onClick={() => onNameFilterChange?.('')}
                aria-label={tBar?.clearNameFilter || 'Clear name filter'}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </th>
  )

  return (
    <div className="rounded-md border bg-card overflow-x-auto">
      {/* `min-w-full` instead of `w-full`: the table fills the container when there's
          slack, but grows past it (triggering the wrapper's overflow-x scroll) once the
          per-column `min-w` floors push the total wider than the viewport. Keeps each
          column legible even when the user expands the forecast window to many months. */}
      <table className="min-w-full border-collapse text-xs">
        <thead>
          {/* Year tier — monthly only. Spans every column of the matching calendar year so
              multi-year windows are unambiguous. Weekly mode skips this tier entirely; the
              ’YY suffix on each month-group label carries year context inline (and stays
              visible across horizontal scroll, where a wide year-tier span tends to
              center its label off-screen). */}
          {!isWeekly && (
            <tr>
              {selectionHeaderCell}
              {workerHeaderCell}
              {yearGroups.map((g, gi) => (
                <th
                  key={`y-${g.year}`}
                  colSpan={g.count}
                  className={cn(
                    'bg-muted text-center text-[11px] tracking-wide font-semibold text-muted-foreground pt-1.5 pb-0.5',
                    gi > 0 && 'border-l-2 border-l-muted-foreground/50'
                  )}
                >
                  {g.year}
                </th>
              ))}
            </tr>
          )}
          {/* Month tier — for weekly mode this is the month-name span across the weeks of
              that month (with ’YY suffix) and is the FIRST header row; for monthly mode
              it's the per-column month header below the year tier. */}
          <tr>
            {isWeekly && selectionHeaderCell}
            {isWeekly && workerHeaderCell}
            {isWeekly
              ? (() => {
                  // Track the cumulative column index of each monthGroup so we can ask
                  // boundaryBorderClass whether that column is on a year boundary.
                  let groupStart = 0
                  return monthGroups.map((g, gi) => {
                    const startIdx = groupStart
                    groupStart += g.count
                    // Pull the actual week-Monday (not the normalized YYYY-MM-01) so
                    // weekGroupHeader can detect single-week groups that bridge months.
                    const firstCell = headerCells[startIdx]
                    const { label, yearSuffix } = weekGroupHeader(firstCell?.monthStart ?? g.monthStartIso, g.count)
                    return (
                      <th
                        key={`mg-${g.yearMonth}-${startIdx}`}
                        colSpan={g.count}
                        className={cn(
                          'bg-muted text-center text-[11px] uppercase tracking-wide font-semibold text-muted-foreground pt-1 pb-1',
                          gi > 0 && (boundaryBorderClass(startIdx, true) || 'border-l-2 border-l-muted-foreground/30')
                        )}
                      >
                        {label}
                        {yearSuffix && (
                          <span className="ml-1 text-[10px] font-normal text-muted-foreground/70 normal-case tracking-normal">
                            ’{yearSuffix}
                          </span>
                        )}
                      </th>
                    )
                  })
                })()
              : headerCells.map((m, i) => {
                  const filter = monthFilters?.[i]
                  const pending = isPendingCell(m)
                  return (
                    <th
                      key={m.monthStart}
                      className={cn(
                        'bg-muted text-center text-[10px] uppercase tracking-wide font-medium text-muted-foreground py-2 min-w-[80px]',
                        boundaryBorderClass(i, false)
                      )}
                    >
                      <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                        <span className={cn(pending && 'opacity-60')}>{monthLabel(m.monthStart)}</span>
                        {m.isCurrentMonth && <span className="text-[8px] opacity-60">{t?.nowLabel || 'NOW'}</span>}
                        {filtersEnabled && !pending && (
                          <MonthFilterPopover
                            monthLabel={m.monthLabel}
                            mode={mode}
                            filter={filter}
                            onChange={(f) => onMonthFilterChange?.(i, f)}
                          />
                        )}
                      </div>
                      {filter && (
                        <div className="text-[9px] font-normal text-blue-600 normal-case tracking-normal">
                          {filter.op === 'gte' ? '≥' : '≤'} {filter.value}%
                        </div>
                      )}
                    </th>
                  )
                })}
          </tr>
          {isWeekly && (
            <tr>
              {headerCells.map((m, i) => {
                const filter = monthFilters?.[i]
                const pending = isPendingCell(m)
                return (
                  <th
                    key={`w-${m.monthStart}`}
                    className={cn(
                      'bg-muted text-center text-[10px] tracking-wide font-medium text-muted-foreground pb-2 min-w-[100px]',
                      boundaryBorderClass(i, true)
                    )}
                    // Full week range on hover.
                    title={weekLabel(m.monthStart)}
                  >
                    <div className="flex items-center justify-center gap-1 whitespace-nowrap">
                      <span className={cn('tabular-nums', pending && 'opacity-60')}>{weekDayRange(m.monthStart)}</span>
                      {m.isCurrentMonth && <span className="text-[8px] opacity-60">{t?.nowLabel || 'NOW'}</span>}
                      {filtersEnabled && !pending && (
                        <MonthFilterPopover
                          monthLabel={m.monthLabel}
                          mode={mode}
                          filter={filter}
                          onChange={(f) => onMonthFilterChange?.(i, f)}
                        />
                      )}
                    </div>
                    {filter && (
                      <div className="text-[9px] font-normal text-blue-600 normal-case tracking-normal">
                        {filter.op === 'gte' ? '≥' : '≤'} {filter.value}%
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          )}
        </thead>
        {workers.length === 0 ? (
          <tbody>
            <tr>
              {/* The td spans every column so the empty row reads as one cell, but the inner
                  message uses `sticky left-0` + `w-fit` so it stays anchored to the visible
                  left edge — without this, weekly mode + a wide axis hides the centered text
                  in the middle of the scroll region. */}
              <td colSpan={headerCells.length + (selectionEnabled ? 2 : 1)} className="p-0">
                <div className="sticky left-0 w-fit px-4 py-6 text-muted-foreground text-sm">
                  {t?.emptyState || 'No workers match the current filters.'}
                </div>
              </td>
            </tr>
          </tbody>
        ) : (
        <tbody>
          {workers.map(w => {
            // Use the precomputed count so we share one current-month resolution with
            // the adapter (which anchors on cells.find(c => c.isCurrentMonth)) instead of
            // assuming cells[0] is the current month.
            const activeNow = w.activeEngagementCount
            const roleAndActive = activeNow > 0
              ? (t?.workerRoleAndActive || '{role} · {n} active')
                .replace('{role}', w.workerRole ?? '')
                .replace('{n}', String(activeNow))
              : null
            const activeOnlyText = activeNow === 1
              ? (t?.activeEngagementSubline || '{n} active engagement').replace('{n}', String(activeNow))
              : (t?.activeEngagementSublinePlural || '{n} active engagements').replace('{n}', String(activeNow))
            const subline = w.workerRole
              ? activeNow > 0 ? roleAndActive : w.workerRole
              : activeNow > 0 ? activeOnlyText : null
            // Show up to two job functions on their own lines; fold the rest behind a hover.
            const jfs = w.jobFunctions ?? []
            const jfFirst = jfs[0]
            const jfSecond = jfs[1]
            const jfRest = jfs.slice(2)
            const isSelected = selectedSet.has(w.userProfileId)
            const cells = cellsForWorker(w)
            // Where the worker's end date lands on the visible axis. Null when
            // they have no end date (T4-salaried workers not pending termination) or it sits past the last
            // visible column — in both cases nothing is marked and nothing greys.
            const endMarker = resolveRowEndMarker(cells.map(c => c.monthStart), w.profileEndDate, granularity)
            const endDateLabel = w.profileEndDate ? formatDayLabelWithYear(w.profileEndDate) : ''
            // Past dates read as "Ended" — a lapsed profile labelled "Ends" is just confusing.
            const hasEnded = !!w.profileEndDate && w.profileEndDate < todayIso
            const endsLabel = (
              hasEnded ? (t?.endedOnLabel || 'Ended {date}') : (t?.endsOnLabel || 'Ends {date}')
            ).replace('{date}', endDateLabel)
            const endBucketTooltip = (t?.endBucketTooltip || 'End date: {date}')
              .replace('{date}', endDateLabel)
            const afterEndTooltip = (t?.afterEndTooltip || 'Past the worker’s end date ({date}) — no capacity to plan against')
              .replace('{date}', endDateLabel)
            const assignedPastEndTooltip = (t?.assignedPastEndTooltip || 'Assigned work past the worker’s end date ({date}) — extend the profile or move the work')
              .replace('{date}', endDateLabel)
            // Flags only the certifications selected in the filter, not every certification
            // the worker holds.
            const expiredCertifications = selectedCertificationSet.size === 0
              ? []
              : (w.certifications ?? []).filter(c =>
                  selectedCertificationSet.has(String(c.certificationId)) && !!c.expiryDate && c.expiryDate < todayIso
                )
            const expiredCertificationNames = expiredCertifications.map(c =>
              resolveCertificationLabel ? resolveCertificationLabel(String(c.certificationId)) : `Certification ${c.certificationId}`
            )
            return (
            <tr
              key={w.userProfileId}
              data-testid={testId('resource-availability-worker-row', w.userProfileId)}
              className={cn(
                'transition-colors border-t',
                isSelected && 'bg-blue-100'
              )}
            >
              {selectionEnabled && (
                <td className={cn(
                  'sticky left-0 z-10 p-0 w-12 min-w-12',
                  isSelected ? 'bg-blue-100' : 'bg-card'
                )}>
                  <div className="flex items-center justify-center px-3 py-2">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleTeamMember?.(w.userProfileId)}
                      aria-label={(t?.teamSelectionAria || 'Add {name} to team timeline').replace('{name}', w.workerName)}
                      className="shrink-0"
                    />
                  </div>
                </td>
              )}
              <td className={cn(
                'sticky z-10 px-4 py-2 whitespace-nowrap',
                selectionEnabled ? 'left-12' : 'left-0',
                isSelected ? 'bg-blue-100' : 'bg-card'
              )}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                  <Link
                    to={`/availability/${w.userProfileId}${suffix}`}
                    aria-label={(t?.viewDetailAria || 'View availability detail for {name}').replace('{name}', w.workerName)}
                    className="group inline-flex items-center rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 text-blue-700 text-[10px] font-semibold mr-2 align-middle no-underline">
                      {w.workerInitials}
                    </span>
                    <span className="inline-block align-middle text-left">
                      <div className="font-medium leading-tight group-hover:underline">{w.workerName}</div>
                      {subline && (
                        <div className="text-[10px] text-muted-foreground leading-tight">{subline}</div>
                      )}
                      {jfFirst && (
                        jfRest.length > 0 ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <div className="text-[10px] text-muted-foreground leading-tight italic truncate max-w-[10rem]">
                                    {jfFirst}
                                  </div>
                                  {jfSecond && (
                                    <div className="text-[10px] text-muted-foreground leading-tight italic truncate max-w-[10rem]">
                                      <span>{jfSecond}</span>
                                      <span className="ml-1 not-italic font-medium text-blue-600">
                                        {(t?.moreSuffix || '+ {n} more').replace('{n}', String(jfRest.length))}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </TooltipTrigger>
                              {/* Portal escapes the sticky cell's z-index stacking context so
                                  subsequent rows don't paint on top of the tooltip. */}
                              <TooltipPrimitive.Portal>
                                <TooltipContent side="right" className="text-xs">
                                  <ul className="list-disc pl-4 space-y-0.5">
                                    {jfs.map(jf => <li key={jf}>{jf}</li>)}
                                  </ul>
                                </TooltipContent>
                              </TooltipPrimitive.Portal>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <>
                            <div className="text-[10px] text-muted-foreground leading-tight italic truncate max-w-[10rem]">
                              {jfFirst}
                            </div>
                            {jfSecond && (
                              <div className="text-[10px] text-muted-foreground leading-tight italic truncate max-w-[10rem]">
                                {jfSecond}
                              </div>
                            )}
                          </>
                        )
                      )}
                    </span>
                  </Link>
                  </div>
                  <WorkerActionsMenu
                    userProfileId={w.userProfileId}
                    workerName={w.workerName}
                    toggleQuery={toggleQuery}
                  />
                </div>
                {/* End-date chip. Carries the exact date as text — the only place it's readable
                    without hovering the flagged cell, and it keeps the date legible when the
                    flagged column has been scrolled out of view. */}
                {endMarker && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                    <Flag className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                    <span className="tabular-nums">{endsLabel}</span>
                  </div>
                )}
                {expiredCertifications.length > 0 && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className="mt-1 inline-flex items-center gap-1 rounded-sm bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-800"
                          data-testid={testId('resource-availability-worker-certification-expired', w.userProfileId)}
                        >
                          <AlertTriangle className="w-2.5 h-2.5 shrink-0" aria-hidden="true" />
                          <span>{t?.expiredCertificationBadge || 'Certification expired'}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipPrimitive.Portal>
                        <TooltipContent side="right" className="text-xs">
                          <ul className="list-disc pl-4 space-y-0.5">
                            {expiredCertificationNames.map((name, i) => <li key={i}>{name}</li>)}
                          </ul>
                        </TooltipContent>
                      </TooltipPrimitive.Portal>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </td>
              {cells.map((cell, i) => {
                // Per-column min-width keeps the data cells legible even with many columns —
                // weekly columns get a wider floor than monthly since their labels and the
                // (smaller) day-range numbers benefit from breathing room.
                const cellMinWidthClass = isWeekly ? 'min-w-[100px]' : 'min-w-[80px]'
                // Year wins over month boundary (priority handled inside boundaryBorderClass);
                // pass `isWeekly` so monthly mode skips the lighter monthly dividers — there
                // every column is already a month, so a per-column border would just be noise.
                const boundaryClass = boundaryBorderClass(i, isWeekly)
                if (isPendingCell(cell)) {
                  return (
                    <td
                      key={`pending-${cell.monthStart}`}
                      className={cn('p-0.5', cellMinWidthClass, boundaryClass)}
                      aria-busy="true"
                    >
                      <div
                        className="flex items-center justify-center min-h-[44px] rounded-sm bg-muted animate-pulse"
                        title={t?.pendingTooltip || 'Loading…'}
                      />
                    </td>
                  )
                }
                // Past the worker's end date they have no capacity at all, so the
                // colour scale would be claiming availability that doesn't exist (an unengaged
                // bucket reads 0% allocated / 100% available). Grey those cells instead; the
                // percentages stay visible but muted so no underlying number is hidden.
                const afterEnd = !!endMarker && i >= endMarker.greyFromIndex
                const isEndBucket = !!endMarker && i === endMarker.endIndex
                // Nothing stops an engagement being staffed past the end date, so a greyed
                // cell can still carry real budgeted hours. That's a genuine conflict no screen
                // flags today — call it out rather than let the grey swallow it.
                const assignedPastEnd = afterEnd && cell.plannedHours > 0
                const cls = afterEnd
                  ? { bg: 'bg-muted', fg: 'text-muted-foreground/70' }
                  : heatmapClasses(cell, mode)
                const label = `${Math.round(mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent)}%`
                return (
                  <td
                    key={cell.monthStart}
                    className={cn('p-0.5', cellMinWidthClass, boundaryClass)}
                  >
                    <div
                      className={cn(
                        'relative flex flex-col items-center justify-center min-h-[44px] rounded-sm font-semibold text-xs',
                        cls.bg,
                        cls.fg,
                        // Text colour only: the cell stays grey (still past the end date) while the
                        // number itself reads as a problem rather than as muted noise.
                        assignedPastEnd && 'text-red-700'
                      )}
                      title={
                        assignedPastEnd
                          ? assignedPastEndTooltip
                          : afterEnd
                            ? afterEndTooltip
                            : cell.overallocated
                              ? (t?.overTooltip || 'Single-day budgeted hours exceeds capacity in this bucket')
                              : undefined
                      }
                    >
                      {/* Marker on the bucket holding the end date: a solid right edge
                          reading as the line availability stops at, plus a flag anchored
                          bottom-left (top-right belongs to the time-off badge). */}
                      {isEndBucket && (
                        <>
                          <span
                            className="absolute inset-y-0 right-0 w-[3px] rounded-r-sm bg-amber-600"
                            aria-hidden="true"
                          />
                          <span
                            className="absolute bottom-0.5 left-0.5 inline-flex items-center text-amber-700"
                            title={endBucketTooltip}
                          >
                            <Flag className="w-2.5 h-2.5" aria-hidden="true" />
                          </span>
                        </>
                      )}
                      {/* Time-off marker (palm tree) for PTO / stat-holiday days in this
                          bucket. Renders nothing when the cell has no time off. Anchored
                          top-right so it doesn't shift the centered percentage. */}
                      <div className="absolute top-0.5 right-0.5">
                        <TimeOffCellBadge
                          ptoDates={cell.ptoDates}
                          statHolidayDates={cell.statHolidayDates}
                          strings={timeOffStrings}
                        />
                      </div>
                      <span>{label}</span>
                      {cell.overallocated && (
                        <span className="text-[8px] opacity-70 leading-tight">{t?.overLabel || 'over'}</span>
                      )}
                    </div>
                  </td>
                )
              })}
            </tr>
            )
          })}
        </tbody>
        )}
      </table>
    </div>
  )
}
